import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface SubscribeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubscribe: (feedUrl: string, name?: string) => Promise<void>;
}

export function SubscribeDialog({ open, onClose, onSubscribe }: SubscribeDialogProps) {
  const [feedUrl, setFeedUrl] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!feedUrl.trim()) return;

    setLoading(true);
    setError(null);
    try {
      await onSubscribe(feedUrl.trim(), name.trim() || undefined);
      setFeedUrl('');
      setName('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  const isValidUrl = feedUrl.trim().length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !loading) {
          setError(null);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Subscribe to Calendar Feed</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="feed-url">ICS Feed URL</Label>
            <Input
              id="feed-url"
              placeholder="https://example.com/calendar.ics"
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="feed-name">Name (optional)</Label>
            <Input
              id="feed-name"
              placeholder="e.g., US Holidays"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValidUrl || loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Subscribe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
